import { Query, SearchType } from '@/components/TeamAndSchedule/types/definitionsType';
import { FacetQuery, FacetItem } from '@/components/TeamAndSchedule/types/facetsTypes';
import { updateUrlParams } from './urlParamsUtils';
import { clearFilterState, updateFilterState } from './filterSessionStorage';
import type { InjectedPagePreferences } from '@agyletime/vue-3-common/src/typings/pagePreference';
import type PagePreference from '@agyletime/vue-3-common/src/typings/pagePreference';
import type { WeekDay } from '@/components/TeamAndSchedule/composables/useWeekStartDay';
import { calculateCurrentWeekDates } from '@/components/TeamAndSchedule/utils/dateUtils';
import { formatQueryParamDate } from '@agyletime/vue-3-common/src/utils/date';

interface FacetPanelRef {
    value?: {
        getFacetVisibilityState?: () => Record<string, string[]>;
    };
}

interface UpdatePagePreferencesParams {
    query: Query;
    pagePreferences: InjectedPagePreferences;
    timezoneSelected: { value: string };
    userTimezone: { value: string };
    searchTypeSelected: { value: SearchType };
    facetPanelRef?: FacetPanelRef;
    selectedView?: { value: string };
    colorMode?: { value: 'TEMPLATES' | 'LOCATIONS' | 'NONE' };
    groupBy?: { value: 'TEAMS' | 'NONE' };
    columnDisplay?: { value: { userId: boolean; email: boolean } };
}

export const buildInitialQuery = (urlQuery: Partial<Query>, timezoneSelected: string, weekStartDay: WeekDay = 1) => {

    const tmpquery: Query = { timezone: timezoneSelected };

    if (urlQuery.facets) {
        tmpquery.facets = urlQuery.facets;
    }
    
    // Add dateFacet if not present at the top level
    if (!urlQuery.facets || (!urlQuery.facets?.teamSchedule && !urlQuery.facets.teamSchedule?.dateFacet)) {
        urlQuery.facets ??= {};
        urlQuery.facets.teamSchedule ??= {} as FacetQuery;
        const [ start, end ] = calculateCurrentWeekDates(weekStartDay).split(',');
        // Normalize the default range boundaries to the selected timezone, mirroring the
        // manual date-selection path (DateRangeFacet.onChangeDate) and the timezone-change
        // path (updateDateFacetBoundaries), both of which use formatQueryParamDate.
        //
        // calculateCurrentWeekDates() returns browser-local midnight serialized as a UTC ISO
        // string. If it is stored raw, getDateRange() (rosterTransformUtils) later re-interprets
        // that instant in the *selected* timezone via moment().tz(tz).startOf('day'). When the
        // selected timezone is behind the browser timezone, that instant falls on the previous
        // calendar day, so the Table View renders an extra day before the range on first load
        // (e.g. an extra "03 Jul" for a 04 Jul–10 Jul selection). Re-applying the filter looked
        // like a fix only because that path already normalizes through formatQueryParamDate.
        const startBoundary = formatQueryParamDate(start, timezoneSelected, 'start');
        const endBoundary = formatQueryParamDate(end, timezoneSelected, 'end');
        const dateFacetData: FacetItem = {
            value: [ start, end ],
            facetId: 'dateFacet',
            facetName: 'Date',
            urlKey: 'date',
            shouldUpdate: false,
            boundaries: `${startBoundary}_${endBoundary}`,
            bdField: ''
        };
        
        urlQuery.facets.teamSchedule.dateFacet = dateFacetData;
        
        tmpquery.facets = { ...urlQuery.facets };
    }

    return tmpquery;
};

export const initializeFromUrlQuery = (
    urlQuery: Partial<Query>,
    refs: {
      timezoneSelected: { value: string };
      userTimezone: { value: string };
      timezoneOptions: { value: { value: string }[] };
      selectedView?: { value: string };
    }
) => {
    if (urlQuery['view.selected']) return;
  
    const {
        timezoneSelected,
        userTimezone,
        timezoneOptions,
        selectedView
    } = refs;
    const {
        timezone: urlTimezone = timezoneOptions
            .value
            .find((t) => t.value === userTimezone.value)!
            .value
    } = urlQuery;
  
    timezoneSelected.value = urlTimezone;
    userTimezone.value     = urlTimezone;
    if (selectedView) {
        selectedView.value = urlQuery['selectedView'] || 'table';
    }
};

export const facetsExist = (facets: Record<string, FacetQuery> | undefined) => {
    return facets && Object.keys(facets).length > 0;
};

export const clearFilters = (refs: {
    resetFilters: { value: boolean };
    timezoneSelected: { value: string };
    userTimezone: { value: string };
    }
) => {
    const { resetFilters, timezoneSelected, userTimezone } = refs;
    resetFilters.value = true;
    
    const currentTimezone = timezoneSelected.value || userTimezone.value;

    // Clear sessionStorage filter state so stale facets aren't restored on reload
    clearFilterState();

    updateUrlParams({
        timezone: currentTimezone,
        facets: undefined
    });
    
    setTimeout(() => {
        resetFilters.value = false;
    }, 0);
};

export const saveCurrentGridSettings = (
    searchTypeSelected: { value: SearchType },
    saveTeamScheduleGridSettings: { value: boolean }
) => {
    if (searchTypeSelected.value === 'teamSchedule') {
        saveTeamScheduleGridSettings.value = true;
    }
    setTimeout(() => {
        saveTeamScheduleGridSettings.value = false;
    }, 0);
};

export const restoreCurrentGridSettings = (
    searchTypeSelected: { value: SearchType },
    restoreTeamScheduleGridSettings: { value: boolean }
) => {
    if (searchTypeSelected.value === 'teamSchedule') {
        restoreTeamScheduleGridSettings.value = true;
    }
    setTimeout(() => {
        restoreTeamScheduleGridSettings.value = false;
    }, 100);
};

export const exportCSV = (
    searchTypeSelected: { value: SearchType },
    exportCSVTeamAndSchedule: { value: boolean }
) => {
    if (searchTypeSelected.value === 'teamSchedule') {
        exportCSVTeamAndSchedule.value = true;
    }
    setTimeout(() => {
        exportCSVTeamAndSchedule.value = false;
    }, 0);
};

function applyTimezoneAndView(
    query: Query,
    timezoneSelected: { value: string },
    userTimezone: { value: string },
    selectedView?: { value: string }
): void {
    if (query.timezone) {
        timezoneSelected.value = query.timezone;
        userTimezone.value = query.timezone;
        if (selectedView) {
            selectedView.value = query.selectedView || 'table';
        }
    }
}

export function buildUrlParamsFromQuery(
    query: Query,
    timezoneSelected: { value: string },
    userTimezone: { value: string },
    selectedView?: { value: string }
): Record<string, string | undefined> {

    const params: Record<string, string | undefined> = {
        timezone: timezoneSelected.value || userTimezone.value,
        selectedView: query.selectedView || 'table'
    };

    applyTimezoneAndView(query, timezoneSelected, userTimezone, selectedView);
    
    if (query.timezone) {
        params.timezone = query.timezone;
    }

    // Store facets in sessionStorage instead of URL to avoid 414 errors
    if (query.facets) {
        updateFilterState({ facets: query.facets });
    }

    // Store columnDisplay in sessionStorage instead of URL
    if (query.columnDisplay) {
        updateFilterState({ columnDisplay: query.columnDisplay });
    }

    // searchDateFilter & dateRange are also routed through updateUrlParams
    // which will store them in sessionStorage via SESSION_STORAGE_KEYS
    params.searchDateFilter = query.searchDateFilter;
    params.dateRange = query.dateRange;

    return params;
}

export function updatePagePreferences({
    query,
    pagePreferences,
    timezoneSelected,
    userTimezone,
    searchTypeSelected,
    facetPanelRef,
    selectedView,
    colorMode,
    groupBy,
    columnDisplay
}: UpdatePagePreferencesParams) {
    const { facets } = query;
    if (!pagePreferences) return;

    const timezone = timezoneSelected.value || userTimezone.value;
    const newIntermediate: Query = {
        timezone,
        searchType: searchTypeSelected.value,
        selectedView: selectedView?.value || 'table'
    };

    // Add colorMode, groupBy, and columnDisplay to the saved view
    addDisplayOptionsToQuery(newIntermediate, colorMode, groupBy, columnDisplay);

    if (shouldIncludeFacets(pagePreferences, facets)) {
        newIntermediate.facets = facets;
    }

    addFacetVisibilityToQuery(newIntermediate, facetPanelRef, searchTypeSelected, pagePreferences);

    pagePreferences.addIntermediate(newIntermediate, 'filter');
}

function addDisplayOptionsToQuery(
    query: Query,
    colorMode?: { value: 'TEMPLATES' | 'LOCATIONS' | 'NONE' },
    groupBy?: { value: 'TEAMS' | 'NONE' },
    columnDisplay?: { value: { userId: boolean; email: boolean } }
) {
    if (colorMode) {
        query.colorMode = colorMode.value;
    }
    if (groupBy) {
        query.groupBy = groupBy.value;
    }
    if (columnDisplay) {
        query.columnDisplay = columnDisplay.value;
    }
}

function addFacetVisibilityToQuery(
    query: Query,
    facetPanelRef: FacetPanelRef | undefined,
    searchTypeSelected: { value: string },
    pagePreferences: InjectedPagePreferences
) {
    const visibilityState = getFacetVisibilityState(facetPanelRef);
    if (visibilityState) {
        const currentVisibilityState = pagePreferences?.intermediate.value?.filter?.facetVisibility;
        query.facetVisibility = {
            ...currentVisibilityState,
            [searchTypeSelected.value]: visibilityState
        };
    }
}

function shouldIncludeFacets(pagePreferences: InjectedPagePreferences, facets: unknown) {
    return Boolean(pagePreferences?.intermediate.value?.filter?.facets) || facetsExist(facets as Record<string, FacetQuery>);
}

function getFacetVisibilityState(facetPanelRef?: FacetPanelRef): Record<string, string[]> | undefined {
    return facetPanelRef?.value?.getFacetVisibilityState?.();
}

export const setPageTitles = (pageTitle: { value: string }, pageSubtitle: { value: string }, view?: PagePreference ) => {
    const searchTitle = 'Team Schedule';

    if (view) {
        pageTitle.value = view.name;
        pageSubtitle.value = searchTitle;
        return;
    }

    pageTitle.value = searchTitle;
    pageSubtitle.value = '';
};